import * as Sentry from '@sentry/react'
import { supabase } from './supabase'
import { createLogger } from '../utils/logger'
import {
  createSessionExpiredError,
  isSessionExpired,
  isSessionExpiredError,
  markSessionExpired,
  redirectToLogin,
} from '../utils/sessionAuth'

const logger = createLogger('api')


const USER_API_URL = import.meta.env.VITE_USER_API_URL
const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL

if (!USER_API_URL) {
  throw new Error('Missing VITE_USER_API_URL. Add it to your environment (see .env.example).')
}
if (!ADMIN_API_URL) {
  throw new Error('Missing VITE_ADMIN_API_URL. Add it to your environment (see .env.example).')
}

class ApiService {
  constructor() {
    this.userUrl = USER_API_URL;
    this.adminUrl = ADMIN_API_URL;
  }

  async _getAccessToken() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }

  async _refreshAccessToken() {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session?.access_token) {
      return null;
    }
    return data.session.access_token;
  }

  async _handleSessionDead() {
    markSessionExpired();
    redirectToLogin();
    throw createSessionExpiredError();
  }

  async _fetch(baseUrl, endpoint, options = {}, isRetry = false) {
    if (isSessionExpired()) {
      throw createSessionExpiredError();
    }

    const token = await this._getAccessToken();
    if (!token) {
      return this._handleSessionDead();
    }

    const url = `${baseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    try {
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        if (isRetry) {
          return this._handleSessionDead();
        }
        const refreshedToken = await this._refreshAccessToken();
        if (refreshedToken) {
          return this._fetch(baseUrl, endpoint, options, true);
        }
        return this._handleSessionDead();
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'API request failed' }));
        const detail = errorData.detail || errorData.message || `HTTP ${response.status}`;
        const err = new Error(detail);
        err.status = response.status;
        throw err;
      }

      if (response.status === 204 || response.status === 205) {
        return null;
      }

      const text = await response.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        throw error;
      }

      const status = error?.status ?? 'network_error';
      Sentry.captureException(error, {
        tags: {
          api_endpoint: endpoint,
          api_status: String(status),
        },
        extra: {
          url,
          method: options.method || 'GET',
        },
      });
      logger.warn('API request failed', {
        endpoint,
        method: options.method || 'GET',
        api_status: String(status),
        url,
      }, error);
      throw error;
    }
  }

  async userRequest(endpoint, options = {}) {
    return this._fetch(this.userUrl, endpoint, options);
  }

  async adminRequest(endpoint, options = {}) {
    return this._fetch(this.adminUrl, endpoint, options);
  }

  // Data methods (User API)
  async getCounties() {
    return this.userRequest('/api/data/counties');
  }

  async getAllCounties() {
    return this.userRequest('/api/data/counties/all');
  }

  async getMyReports() {
    return this.userRequest('/api/data/my-reports');
  }

  async queryData(queryParams) {
    return this.userRequest('/api/data/query-by-county', {
      method: 'POST',
      body: JSON.stringify(queryParams),
    });
  }

  async getCountyCounts() {
    return this.userRequest('/api/data/county-counts');
  }

  async getDataTypeCounts(queryParams) {
    return this.userRequest('/api/data/datatype-counts-by-county', {
      method: 'POST',
      body: JSON.stringify(queryParams),
    });
  }

  async getCountyBoundary(countySlug) {
    return this.userRequest(`/api/data/counties/${encodeURIComponent(countySlug)}/boundary`);
  }

  async getMunicipalityBoundaries() {
    return this.userRequest('/api/data/municipality-boundaries');
  }

  async getCountyFilterMunicipalities(countySlug) {
    return this.userRequest(`/api/data/counties/${encodeURIComponent(countySlug)}/filter-municipalities`);
  }

  // --- Case Actions ---

  async putReportAction(reportId, { triage_status, is_following }) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/action`, {
      method: 'PUT',
      body: JSON.stringify({ triage_status, is_following }),
    });
  }

  async deleteReportAction(reportId) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/action`, {
      method: 'DELETE',
    });
  }

  async getReportActions() {
    return this.userRequest('/api/data/reports/actions');
  }

  // --- User Lists ---

  async getLists() {
    return this.userRequest('/api/data/lists');
  }

  async createList(name) {
    return this.userRequest('/api/data/lists', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateList(listId, updates) {
    return this.userRequest(`/api/data/lists/${encodeURIComponent(listId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteList(listId) {
    return this.userRequest(`/api/data/lists/${encodeURIComponent(listId)}`, {
      method: 'DELETE',
    });
  }

  // --- Saved Filters ---

  async getSavedFilters() {
    return this.userRequest('/api/data/saved-filters');
  }

  async createSavedFilter(name, filterConfig) {
    return this.userRequest('/api/data/saved-filters', {
      method: 'POST',
      body: JSON.stringify({ name, filter_config: filterConfig }),
    });
  }

  async updateSavedFilter(filterId, updates) {
    const body = {};
    if (updates.name != null) body.name = updates.name;
    if (updates.filter_config != null) body.filter_config = updates.filter_config;
    if (updates.position != null) body.position = updates.position;
    return this.userRequest(`/api/data/saved-filters/${encodeURIComponent(filterId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deleteSavedFilter(filterId) {
    return this.userRequest(`/api/data/saved-filters/${encodeURIComponent(filterId)}`, {
      method: 'DELETE',
    });
  }

  async addListItem(listId, reportId) {
    return this.userRequest(`/api/data/lists/${encodeURIComponent(listId)}/items`, {
      method: 'POST',
      body: JSON.stringify({ report_id: reportId }),
    });
  }

  async removeListItem(listId, reportId) {
    return this.userRequest(`/api/data/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(reportId)}`, {
      method: 'DELETE',
    });
  }

  // --- Report Notes ---

  async getReportNotes(reportId) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/notes`);
  }

  async createReportNote(reportId, content) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async updateReportNote(reportId, noteId, content) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/notes/${encodeURIComponent(noteId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  }

  async deleteReportNote(reportId, noteId) {
    return this.userRequest(`/api/data/reports/${encodeURIComponent(reportId)}/notes/${encodeURIComponent(noteId)}`, {
      method: 'DELETE',
    });
  }

  // --- Admin Methods (Admin API) ---

  async getAllUsers() {
    return this.adminRequest('/api/admin/users');
  }

  async getUserCountyAccess(userId) {
    return this.adminRequest(`/api/admin/user/${userId}/regions`);
  }

  async updateUserCountyAccess(userId, countyIds) {
    return this.adminRequest(`/api/admin/user/${userId}/regions`, {
      method: 'POST',
      body: JSON.stringify(countyIds),
    });
  }

  async createDocumentRecord(payload) {
    return this.adminRequest('/api/admin/documents', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteCountyDocument(documentId) {
    return this.adminRequest(`/api/admin/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async getCountyDocuments(countyId) {
    return this.adminRequest(`/api/admin/counties/${countyId}/documents`);
  }

  async clearCountyDocuments(countyId) {
    return this.adminRequest(`/api/admin/counties/${countyId}/documents`, {
      method: 'DELETE',
    });
  }

  async resetCountyProcessing(countyId) {
    return this.adminRequest(`/api/admin/counties/${countyId}/reset-processing`, {
      method: 'POST',
    });
  }

  async startCountyReportGeneration(countyId, forceRerun = false, documentIds = null) {
    const params = forceRerun ? '?force_rerun=true' : '';
    const body = documentIds && documentIds.length > 0 ? { document_ids: documentIds } : {};
    return this.adminRequest(`/api/admin/counties/${countyId}/generate-reports${params}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getReportGenerationTask(taskId) {
    return this.adminRequest(`/api/admin/report-generation-tasks/${taskId}`);
  }

  async getCountyReportGenerationTasks(countyId) {
    return this.adminRequest(`/api/admin/counties/${countyId}/report-generation-tasks`);
  }

  async getQueue() {
    return this.adminRequest('/api/admin/queue');
  }

  async reorderQueue(orderedTaskIds) {
    return this.adminRequest('/api/admin/queue/reorder', {
      method: 'POST',
      body: JSON.stringify({ ordered_task_ids: orderedTaskIds }),
    });
  }

  async restartTask(taskId) {
    return this.adminRequest(`/api/admin/queue/task/${taskId}/restart`, {
      method: 'POST',
    });
  }

  async getQueueHistory() {
    return this.adminRequest('/api/admin/queue/history');
  }

  async getCountyDocumentStatus() {
    return this.adminRequest('/api/admin/counties/document-status');
  }

  async getTigerStates() {
    return this.adminRequest('/api/admin/tiger/states');
  }

  async getTigerCounties(stateFips) {
    return this.adminRequest(`/api/admin/tiger/states/${encodeURIComponent(stateFips)}/counties`);
  }

  async createCountyFromTiger(payload) {
    return this.adminRequest('/api/admin/counties/from-tiger', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteCounty(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}`, {
      method: 'DELETE',
    });
  }

  async getAdminMunicipalities() {
    return this.adminRequest('/api/admin/municipalities');
  }

  async setCountyPublished(countyId, is_published) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/published`, {
      method: 'PATCH',
      body: JSON.stringify({ is_published }),
    });
  }

  async setMunicipalityPublished(municipalityId, is_published) {
    return this.adminRequest(`/api/admin/municipalities/${encodeURIComponent(municipalityId)}/published`, {
      method: 'PATCH',
      body: JSON.stringify({ is_published }),
    });
  }

  async getCatalogPublishingTree() {
    return this.adminRequest('/api/admin/catalog/publishing-tree');
  }

  async bulkPublishCountyMunicipalities(countyId, is_published) {
    return this.adminRequest(
      `/api/admin/counties/${encodeURIComponent(countyId)}/municipalities/publish-all`,
      {
        method: 'POST',
        body: JSON.stringify({ is_published }),
      }
    );
  }

  async getScrapingRegions(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/scraping-regions`);
  }

  async saveScrapingConfig(payload) {
    return this.adminRequest('/api/admin/scraping-sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getCountiesMunicipalityStatus() {
    return this.adminRequest('/api/admin/counties/municipality-status');
  }

  async getCountiesScrapingStatus() {
    return this.adminRequest('/api/admin/counties/scraping-status');
  }

  async getCountiesScrapingCompletionStatus() {
    return this.adminRequest('/api/admin/counties/scraping-completion-status');
  }

  async getMunicipalityOverlaps(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/municipality-overlaps`);
  }

  async getExistingMunicipalityAssignments(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/municipality-assignments`);
  }

  async saveMunicipalityAssignments(countyId, assignments) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/municipality-assignments`, {
      method: 'POST',
      body: JSON.stringify(assignments),
    });
  }

  async createScrapingTasks(scrapingDataId, options = {}) {
    const { sourceSystem, config } = options;
    const query = sourceSystem ? `?source_system=${encodeURIComponent(sourceSystem)}` : '';
    return this.adminRequest(
      `/api/admin/scraping-sources/${encodeURIComponent(scrapingDataId)}/tasks${query}`,
      {
        method: 'POST',
        body: JSON.stringify(config ? { config } : {}),
      },
    );
  }

  async getScrapingTasks(scrapingDataId) {
    return this.adminRequest(`/api/admin/scraping-sources/${encodeURIComponent(scrapingDataId)}/tasks`);
  }

  async queueAllScrapingTasksForCounty(countyId, config = null) {
    return this.adminRequest(
      `/api/admin/counties/${encodeURIComponent(countyId)}/queue-all-scraping-tasks`,
      {
        method: 'POST',
        body: JSON.stringify(config ? { config } : {}),
      },
    );
  }

  async getAllScrapingTasksForCounty(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/scraping-tasks`);
  }

  async getScrapingDefaultConfig() {
    return this.adminRequest('/api/admin/scraping/default-config');
  }

  async saveScrapingDefaultConfig(overrides) {
    return this.adminRequest('/api/admin/scraping/default-config', {
      method: 'PUT',
      body: JSON.stringify(overrides),
    });
  }

  async clearScrapingDefaultConfig() {
    return this.adminRequest('/api/admin/scraping/default-config', {
      method: 'DELETE',
    });
  }

  async getScrapingTaskManifest(taskId) {
    return this.adminRequest(`/api/admin/scraping-tasks/${encodeURIComponent(taskId)}/manifest`);
  }

  async retryFailedScrapingUrls(scrapingDataId, statuses) {
    return this.adminRequest(`/api/admin/scraping-sources/${encodeURIComponent(scrapingDataId)}/retry-failed`, {
      method: 'POST',
      body: JSON.stringify(statuses ? { statuses } : {}),
    });
  }

  async clearCountyPipelineData(countyId) {
    return this.adminRequest(`/api/admin/counties/${encodeURIComponent(countyId)}/clear-pipeline-data`, {
      method: 'DELETE',
    });
  }

  async getReportGenDefaultConfig() {
    return this.adminRequest('/api/admin/report-generation/default-config');
  }

  async saveReportGenDefaultConfig(value) {
    return this.adminRequest('/api/admin/report-generation/default-config', {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async clearReportGenDefaultConfig() {
    return this.adminRequest('/api/admin/report-generation/default-config', {
      method: 'DELETE',
    });
  }

  async getReportEntries({ search, countyId, limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (countyId) params.set('county_id', countyId);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.adminRequest(`/api/admin/report-entries?${params}`);
  }

  async getReportTrace(entryId) {
    return this.adminRequest(`/api/admin/report-entries/${encodeURIComponent(entryId)}/trace`);
  }

  async getReportsForReview({ countyId, publishStatus } = {}) {
    const params = new URLSearchParams();
    if (countyId) params.set('county_id', countyId);
    if (publishStatus) params.set('publish_status', publishStatus);
    return this.adminRequest(`/api/admin/reports/review?${params}`);
  }

  async publishReports(reportEntryIds) {
    return this.adminRequest('/api/admin/reports/publish', {
      method: 'POST',
      body: JSON.stringify({ report_entry_ids: reportEntryIds }),
    });
  }

  async unpublishReport(publishedReportId) {
    return this.adminRequest(
      `/api/admin/reports/${encodeURIComponent(publishedReportId)}/unpublish`,
      { method: 'PATCH' },
    );
  }

  async republishReport(publishedReportId) {
    return this.adminRequest(
      `/api/admin/reports/${encodeURIComponent(publishedReportId)}/republish`,
      { method: 'PATCH' },
    );
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;